import { apiService } from '../../../services/apiService';
import { API_PATHS } from '../../../utils/apiPaths';
import {
  getDccMarkupRatesForTerminalPath,
  getDeactivateDccMarkupRatePath,
  getTerminalDccStatusPath,
} from '../../../utils/apiPaths';
import type { DccMarkupRateRow, SelectOption } from '../models/types';
import { mapDccMarkupRateRow } from '../utils/utils';

export async function fetchActiveMerchants(): Promise<SelectOption[]> {
  const res: any = await apiService.post(API_PATHS.GET_MERCHANTS, { page: 0, size: 200, status: 'ACTIVE' });
  if (!res?.success) return [];

  const pageData = res?.data?.content ? res.data : res;
  const rows = pageData.content || [];
  return rows
    .map((m: Record<string, unknown>) => ({
      id: Number(m.id),
      name: String(m.name || `Merchant ${m.id}`),
    }))
    .filter((m: SelectOption) => m.id > 0);
}

export async function fetchMerchantTerminals(merchantId: number): Promise<SelectOption[]> {
  const res: any = await apiService.post(API_PATHS.GET_MERCHANT_TERMINALS, { merchantId });
  const list = Array.isArray(res?.data?.content ?? res?.data) ? (res?.data?.content ?? res?.data) : [];
  return list
    .map((t: Record<string, unknown>) => ({
      id: Number(t.id ?? t.autoId ?? t.terminalId ?? 0),
      name: String(t.name ?? t.terminalName ?? `Terminal ${t.id ?? ''}`),
    }))
    .filter((t: SelectOption) => t.id > 0);
}

export async function fetchDccMarkupRates(terminalId: number): Promise<DccMarkupRateRow[]> {
  const res: any = await apiService.get(getDccMarkupRatesForTerminalPath(terminalId));
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to load DCC markup rates.');
  }

  const list = Array.isArray(res.data) ? res.data : [];
  return list.map((item: Record<string, unknown>) => mapDccMarkupRateRow(item));
}

export async function fetchTerminalDccStatus(terminalId: number): Promise<boolean> {
  const res: any = await apiService.get(getTerminalDccStatusPath(terminalId));
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to load DCC status for this terminal.');
  }

  if (res?.data?.dccEnabled != null) return Boolean(res.data.dccEnabled);
  if (res?.data?.enabled != null) return Boolean(res.data.enabled);
  if (res?.enabled != null) return Boolean(res.enabled);
  return false;
}

export async function setTerminalDccStatus(terminalId: number, enabled: boolean) {
  const res: any = await apiService.patch(getTerminalDccStatusPath(terminalId), null, {
    params: { enabled },
  });

  if (!res?.success) {
    throw new Error(res?.message || 'Failed to update DCC status.');
  }

  return res;
}

export async function addDccMarkupRate(payload: {
  merchantTerminalId: number;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  notes?: string | null;
}) {
  const res: any = await apiService.post(API_PATHS.ADD_DCC_MARKUP_RATE, payload);
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to save DCC markup rate.');
  }
  return res;
}

export async function deactivateDccMarkupRate(rateId: number) {
  await apiService.patch(getDeactivateDccMarkupRatePath(rateId));
}
